<template>
  <div class="base-layout d-flex">
    <div
      class="base-layout--left bg-white-custom"
      :class="{
        'collapse-left': !baseLayoutSidebarOpened,
        'expand-left': baseLayoutSidebarOpened,
        'isMobile': isMobile
      }"
    >
      <div v-if="isMobile" class="base-layout--left-close" @click="closeSidebar">
        <b-icon icon="x-lg"></b-icon>
      </div>
      <div ref="baseLayoutLeftRef" class="base-layout--left-top d-flex align-items-center pl-3">
        <router-link :to="homePath">
          <div class="d-flex align-items-center">
            <img src="/images/rcl-logo-badge-50.png" alt="homepage" />
            <img src="/images/Convey911_logo_text-145x25.png" alt="homepage" />
          </div>
        </router-link>
      </div>
      <div class="base-layout--left-bottom d-flex flex-column">
        <div v-if="!isCommonModule && !isAdminModule" class="d-block d-xl-none px-3 pb-3">
          <ModuleNavigatorPicker :isDarkMode="isDarkMode" />
        </div>
        <div style="flex: 1; max-height: 100%;">
          <UserLeftSidebar v-if="$auth.isAuthenticated && isCommonModule" />
          <AdminLeftSidebar v-if="isFullyAuthenticated && isAdminModule" />
          <ConvoLeftSidebar v-if="isFullyAuthenticated && isCommunicatorModule && showModule('core:communicatorEnabled')" />
          <ConnectLeftSidebar v-if="isFullyAuthenticated && isConnectModule && showModule('core:connectEnabled')" />
          <CampaignLeftSidebar v-if="isFullyAuthenticated && isCampaignModule && showModule('core:alertEnabled')" />
          <TokenLoader v-if="isFullyAuthenticated" />
        </div>
      </div>
    </div>
    <div
      class="base-layout--right"
      :class="{
        'collapse-right': baseLayoutSidebarOpened,
        'expand-right': !baseLayoutSidebarOpened,
        'isMobile': isMobile
      }"
    >
      <div class="base-layout--right-top">
        <TopBar
          :sidebarIndicator="sidebarIndicator"
          @logout="$emit('logout')"
          @login="$emit('login')"
        />
      </div>
      <div class="base-layout--right-bottom">
        <router-view></router-view>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import TopBar from "@/components/TopBar";
import ConvoLeftSidebar from "@/components/ConvoLeftSidebar";
import AdminLeftSidebar from "@/components/admin/AdminLeftSidebar";
import ConnectLeftSidebar from "@/components/convey-connect/ConnectLeftSidebar";
import CampaignLeftSidebar from "@/components/campaigns/CampaignLeftSidebar";
import UserLeftSidebar from "@/components/user/UserLeftSidebar";
import TokenLoader from "@/components/TokenLoader";
import ModuleNavigatorPicker from "@/components/ModuleNavigatorPicker";

export default {
  name: "BaseLayout",
  components: {
    TopBar,
    AdminLeftSidebar,
    ConvoLeftSidebar,
    ConnectLeftSidebar,
    CampaignLeftSidebar,
    UserLeftSidebar,
    TokenLoader,
    ModuleNavigatorPicker,
  },
  data() {
    return {
      currentModule: null,
      currentModules: [],
      commonModuleRouteNames: [
        "User Home",
        "User Profile",
        "User Message Templates",
      ],
      communicatorModuleRouteNames: [
        "Conversations Dashboard",
        "Conversation Detail",
      ],
      adminModuleRouteNames: [
        "Admin Dashboard",
        "Admin Settings",
        "Admin Conversations",
        "Admin Conversation Detail",
        "Recent Activities",
        "Admin Users",
        "Admin Create User",
        "Admin Edit User",
        "Admin User Invitations",
        "Admin User Invitation Detail",
        "Admin Numbers",
        "Admin Create Number",
        "Admin Edit Number",
        "Admin Templates",
        "Admin Create Template",
        "Admin Edit Template",
        "Admin Call Positions",
        "Admin Create Call Position",
        "Admin Edit Call Position",
      ],
      connectModuleRouteNames: [
        "Convey Connect Dashboard",
        "Convey Connect Calls",
        "Convey Connect Settings",
      ],
      campaignModuleRouteNames: [
        "Campaigns",
        "Campaign Dashboard",
        "Campaign Events",
        "Campaign Event Detail",
        "Campaign Subscribers",
        "New Campaign Subscriber",
        "Campaign Subscriber Detail",
      ],
      sidebarIndicator: false,
    };
  },
  computed: {
    ...mapGetters("user", [
      "userSidebarOpened",
      "isDarkMode",
      "isMobile"
    ]),
    ...mapGetters("agency", [
      "currentAgency",
      "isAgencyConnecting",
      "agencySettings", "agencyModules",
      "selectedAgencyModule"
    ]),
    ...mapGetters("campaigns", ["currentCampaign"]),
    homePath() {
      if (this.isCommunicatorModule) {
        return "/conversations";
      } else if (this.isCampaignModule) {
        return !this.currentCampaign ? "/campaigns" : `/campaigns/${this.currentCampaign.campaign_id}`;
      } else if (this.isConnectModule) {
        return "/connect";
      } else {
        return "/";
      }
    },
    isFullyAuthenticated() {
      return !!this.currentAgency && this.$auth.isAuthenticated;
    },
    isCommonModule() {
      return this.commonModuleRouteNames.includes(this.$route.name);
    },
    isCommunicatorModule() {
      return this.communicatorModuleRouteNames.includes(this.$route.name);
    },
    isAdminModule() {
      return this.adminModuleRouteNames.includes(this.$route.name);
    },
    isConnectModule() {
      return this.connectModuleRouteNames.includes(this.$route.name);
    },
    isCampaignModule() {
      return this.campaignModuleRouteNames.includes(this.$route.name);
    },
    baseLayoutSidebarOpened() {
      const routeNamesNotNeedSidebar = [
        "Home",
        "Page Not Found",
        "Unknown Page"
      ];
      return this.userSidebarOpened && !routeNamesNotNeedSidebar.includes(this.$route.name);
    },
  },
  methods: {
    closeSidebar() {
      this.$store.commit("user/setUserSidebarOpened", !this.userSidebarOpened);
      this.sidebarIndicator = false;
    },
    showModule(moduleName) {
      return this.currentAgency
        ? this.agencySettings[moduleName] === true || this.agencySettings[moduleName] === "true"
        : false;
    },
    goHome() {
      this.$router.push({ path: "/" }).catch(() => {});
    },
    clickAgencyModule(module) {
      if (module) {
        this.$store.commit('agency/setSelectedAgencyModule', module);
        this.$router.push({ name: module.routeName }).catch(() => {});
      }
    }
  },
  watch: {
    selectedAgencyModule: {
      handler(module) {
        if (module) {
          this.currentModule = { ...module };
        } else {
          this.currentModule = null;
        }
      },
      immediate: true,
      deep: true,
    },
    agencyModules: {
      handler(modules) {
        this.currentModules = [...modules];
      },
      deep: true,
      immediate: true,
    },
    isMobile: {
      handler(isMobile) {
        this.$store.commit("user/setUserSidebarOpened", !isMobile);
        this.sidebarIndicator = isMobile;
      },
      immediate: true,
    }
  }
};
</script>

<style lang="scss">
.base-layout {
  position: relative;
  &--left {
    position: relative;
    z-index: 2;

    &-close {
      position: absolute;
      right: 1rem;
      top: 1rem;
      cursor: pointer;
    }

    &.isMobile {
      position: absolute;
      height: 100vh;
      transition: left ease 300;
      @supports (height: 100svh) {
        height: 100svh;
      }
    }
    &.collapse-left {
      visibility: hidden;
      width: 0px;
      &.isMobile {
        visibility: visible;
        width: 320px;
        top: 0;
        left: -320px;
        box-shadow: none;
      }
    }
    &.expand-left {
      visibility: visible;
      width: 320px;
      &.isMobile {
        top: 0;
        left: 0;
        box-shadow: 0 4px 8px 0 rgb(0 0 0 / 0%), 0 6px 20px 0 rgb(0 0 0 / 12%);
      }
    }
    &-top {
      z-index: 2;
      width: 100%;
      height: 70px;
    }
    &-bottom {
      z-index: 1;
      width: 100%;
      height: calc(100vh - 70px);

      @supports (height: 100svh) {
        width: 100%;
        height: calc(100svh - 70px);
        z-index: 1;
      }
    }
  }
  &--right {
    z-index: 1;

    &.expand-right {
      width: 100vw;
      @supports (width: 100svw) {
        width: 100svw;
      }
    }
    &.collapse-right {
      width: calc(100vw - 320px);
      &.isMobile {
        width: 100vw;
      }
      @supports (width: 100svw) {
        width: calc(100svw - 320px);
        &.isMobile {
          width: 100svw;
        }
      }
    }
    &-top {
      z-index: 2;
      width: 100%;
      height: 70px;

      @media screen and (max-width: 767px) {
        height: 110px;
      }
    }
    &-bottom {
      z-index: 1;
      width: 100%;
      height: calc(100vh - 70px);

      >.b-overlay-wrap {
        width: 100%;
        height: 100%;
      }

      @media screen and (max-width: 767px) {
        height: calc(100vh - 110px);
      }

      @supports (height: 100svh) {
        width: 100%;
        height: calc(100svh - 70px);
        z-index: 1;

        @media screen and (max-width: 767px) {
          height: calc(100svh - 110px);
        }
      }
    }
  }
}
</style>
